<template>
  <v-container fluid class="pa-4">
    <v-card>
      <v-card-title class="d-flex justify-space-between">
        <span class="title">{{ $t('title.workPosition') }}</span>
        <div class="d-flex">
          <v-tooltip location="bottom" content-class="custom-tooltip">
            <template #activator="{ props }">
              <v-btn v-bind="props" size="small" variant="outlined" color="primary" class="mr-2 action-btn"
                icon="mdi-eye" @click="printWorkPositions" />
            </template>
            {{ $t('general.viewprint') }}
          </v-tooltip>

          <v-tooltip location="bottom" content-class="custom-tooltip">
            <template #activator="{ props }">
              <v-btn v-bind="props" size="small" variant="outlined" color="primary" class="action-btn" icon="mdi-plus"
                @click="addPosition" />
            </template>
            {{ $t('general.addworkPosition') }}
          </v-tooltip>

        </div>
      </v-card-title>

      <v-card-text>
        <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" class="mb-2" autocomplete="off"
          density="compact" />

        <v-data-table :headers="headers" :items="filteredPositions" :loading="loading"
          :loading-text="$t('general.loading')" class="min-width-table custom-header" :items-per-page="15">

          <!-- Bold naslovi i sort ikonice -->
          <template v-slot:headers="{ columns, toggleSort, isSorted, getSortIcon }">
            <tr>
              <th v-for="column in columns" :key="column.value" @click="() => column.sortable && toggleSort(column)"
                :style="{
                  cursor: column.sortable ? 'pointer' : 'default',
                  width: column.width,
                  maxWidth: column.width,
                  textAlign: column.value === 'actions' ? 'right' : 'left'
                }" class="sortable-header">
                <div class="d-flex align-center w-100" :class="column.value === 'actions'
                  ? 'justify-end'
                  : 'justify-space-between'">
                  <strong>{{ column.text }}</strong>

                  <v-icon v-if="column.sortable" size="12" class="sort-icon ml-2" :class="{ active: isSorted(column) }">
                    {{ isSorted(column) ? getSortIcon(column) : 'mdi-arrow-up-down' }}
                  </v-icon>
                </div>


              </th>
            </tr>
          </template>

          <!-- Naziv -->
          <template v-slot:[`item.Name`]="{ item }">
            {{ item.Name }}
          </template>

          <!-- Akcije -->
          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex align-center justify-end action-buttons">
              <v-tooltip location="bottom" content-class="custom-tooltip">
                <template #activator="{ props }">
                  <v-btn v-bind="props" size="small" variant="outlined" color="primary" class="mr-2 action-btn"
                    @click="editPosition(item)">
                    <v-icon size="16">mdi-pencil</v-icon>
                  </v-btn>
                </template>
                {{ $t('general.uredi') }}
              </v-tooltip>

              <v-tooltip location="bottom" content-class="custom-tooltip">
                <template #activator="{ props }">
                  <v-btn v-bind="props" size="small" variant="outlined" color="red" class="action-btn"
                    @click="openDeleteDialog(item)">
                    <v-icon size="16">mdi-delete</v-icon>
                  </v-btn>
                </template>
                {{ $t('general.delete') }}
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Dijalog za dodavanje/uređivanje radne pozicije -->
    <BaseModal v-model="dialog"
      :title="editMode ? this.$t('modalni.workPosition.uredi') : this.$t('modalni.workPosition.dodaj')">
      <v-form ref="workpositionPageForm" v-model="isFormValid">
        <v-text-field v-model="form.name" :label="$t('table.workPosition.naziv')" :rules="[rules.required]"
          class="my-text" required />
      </v-form>

      <template #footer>
        <div class="d-flex justify-end ga-2">
          <!-- OTKAŽI -->
          <v-btn variant="outlined" @click="closeDialog">
            <span style="font-size:14px; font-weight: 600;">{{ $t('general.cancel') }}</span>
          </v-btn>

          <!-- SAČUVAJ -->
          <v-btn color="primary" variant="flat" @click="savePosition">
            <span style="font-size:14px; font-weight: 600;">{{ $t('general.save') }}</span>
          </v-btn>
        </div>
      </template>

    </BaseModal>


    <!-- Dijalog za potvrdu brisanja radne pozicije -->
    <BaseModal v-model="deleteDialog" :title="this.$t('modalni.workPosition.brisi')">
      <p>
        {{ $t('User_message.m86') }} <strong>{{ deletePositionName }}</strong>? <br /><br />
        {{ $t('User_message.m88') }} {{ workersOnPosition }} {{ $t('User_message.m89') }}
      </p>

      <template #footer>
        <div class="d-flex justify-end ga-2">
          <!-- OTKAŽI -->
          <v-btn variant="outlined" @click="deleteDialog = false">
            <span style="font-size:14px; font-weight: 600;">{{ $t('general.cancel') }}</span>
          </v-btn>

          <!-- OBRIŠI -->
          <v-btn color="danger" variant="flat" @click="confirmDelete">
            <span style="font-size:14px; font-weight: 600; color: #FFF">{{ $t('general.deleteButton') }}</span>
          </v-btn>
        </div>
      </template>
    </BaseModal>

    <!--Save/Print dialog-->
    <BaseModal v-model="reportPreview" size="xl" :title="$t('print.listaRadnihPozicija')" height="90vh"
      modalClass="report-modal" :hideFooter="true">

      <template #header-actions>

        <v-tooltip location="bottom" content-class="custom-tooltip" :text="$t('general.download')">
          <template #activator="{ props }">
            <v-btn v-bind="props" icon variant="plain" @click="downloadPDF">
              <v-icon size="18"
                style="padding-top: 2px !important; padding-left: 10px !important;">mdi-download</v-icon>
            </v-btn>
          </template>
        </v-tooltip>

        <v-tooltip location="bottom" content-class="custom-tooltip" :text="$t('general.print')">
          <template #activator="{ props }">
            <v-btn v-bind="props" icon variant="plain" @click="printReport">
              <v-icon size="18">mdi-printer</v-icon>
            </v-btn>
          </template>
        </v-tooltip>

      </template>

      <iframe ref="reportFrame" :style="{
        width: '100%',
        height: '100%',
        border: 'none',
        transform: `scale(${zoom})`,
        transformOrigin: 'top left'
      }" />

    </BaseModal>


  </v-container>
</template>

<script>
import BaseModal from "@/components/dialogs/BaseModal.vue";
import { printTable, fetchHeaderImage } from "@/services/printService";
import html2pdf from "html2pdf.js"
import { format } from "date-fns";

export default {
  name: "WorkPositionsPage",
  data() {
    return {
      html: '',
      zoom: 1,
      reportPreview: false,
      workersOnPosition: 0,
      deleteDialog: false,
      deletePositionId: null,
      deletePositionName: "",
      search: "",
      loading: false,
      positions: [],
      dialog: false,
      editMode: false,
      selectedId: null,
      isFormValid: false,

      form: { name: "" },

      rules: {
        required: v => !!v || this.$t('systemMessage.m1'),
      },

      headers: [
        { text: this.$t('table.workPosition.naziv'), value: "Name", sortable: true, width: 'calc(100% - 100px)' },
        { text: this.$t('general.optionsUPPER'), value: "actions", sortable: false, width: '100px' },
      ],
    };
  },

  computed: {
    filteredPositions() {
      const term = this.search.toLowerCase().trim();

      const filtered = this.positions.filter(p =>
        Object.values(p).some(val =>
          String(val ?? "").toLowerCase().includes(term)
        )
      );

      return filtered;
    },
  },

  components: {
    BaseModal,
  },
  mounted() {
    this.fetchPositions();
  },

  methods: {
    generateReportFileName(title) {

      const date = new Date()

      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()

      const compactDate = `${day}${month}${year}`

      const cleanTitle = title
        .replace(/[čć]/g, "c")
        .replace("ž", "z")
        .replace("š", "s")
        .replace("đ", "d")
        .split(" ")
        .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join("")

      return `${cleanTitle}_${compactDate}.pdf`
    },
    openReportPreview(html) {

      this.reportPreview = true

      this.$nextTick(() => {
        this.$nextTick(() => {

          const iframe = this.$refs.reportFrame
          if (!iframe) return

          const doc = iframe.contentWindow.document

          doc.open()
          doc.write(html)
          doc.close()

        })
      })
    },

    printReport() {
      const iframe = this.$refs.reportFrame
      iframe.contentWindow.print()
    },

    downloadPDF() {

      const container = document.createElement("div")
      container.innerHTML = this.html

      const title = container.querySelector("h2")?.innerText

      const fileName = this.generateReportFileName(title)

      html2pdf()
        .set({
          margin: 10,
          filename: fileName,
          html2canvas: { scale: 2 },
          jsPDF: {
            unit: "mm",
            format: "a4",
            orientation: "landscape"
          }
        })
        .from(container)
        .save()
    },

    async printWorkPositions() {
      const positions = this.filteredPositions || []

      if (!positions.length) {
        this.$store.dispatch("NOTIF_WARNING", {
          poruka: this.$t("print.na"),
          position: "center",
          icon: "error",
          timer: 4500,
          width: "550",
        })
        return
      }

      const headerImg = await fetchHeaderImage(this.$axios)

      this.html = printTable({
        title: this.$t("print.listaRadnihPozicija"),
        columns: [
          { key: "name", label: this.$t("table.workPosition.naziv"), width: "100%" }
        ],
        data: positions.map(p => ({
          name: p.Name ?? "-"
        })),
        headerImg,
        footer: `${this.$t("exportToExcel.pokrenuo")} ${localStorage.getItem("Name")} <br />
    ${this.$t("print.m1")} ${format(new Date(), "dd.MM.yyyy.")}`,
      })
      this.openReportPreview(this.html)
    },
    async openDeleteDialog(item) {
      this.deletePositionId = item.Id;
      this.deletePositionName = item.Name;

      // reset
      this.workersOnPosition = 0;

      var data = {
        CompanyId: Number(localStorage.getItem('CompanyId')),
        WorkPositionId: item.Id
      }

      try {
        await this.$axios.post('Category/NoWorkers', data)
          .then(r => {
            this.workersOnPosition = r.data?.number ?? 0;
          })
      } catch (e) {
        console.error(e);
        this.workersOnPosition = 0;
      }

      this.deleteDialog = true;
    },

    cancelDelete() {
      this.deleteDialog = false;
      this.deletePositionId = null;
      this.deletePositionName = "";
    },
    async confirmDelete() {
      if (!this.deletePositionId) return;

      try {
        const r = await this.$axios.put(
          'Role/WorkPosition/Delete/' + this.deletePositionId
        );

        if (r.data === "UPDATED" || r.data === "DELETED" || r.status === 200) {
          this.$store.dispatch("NOTIF_SUCCESS", {
            poruka: this.$t('User_message.m19'),
            position: "top-end",
            timer: 2500,
            action: "noreload"
          });

          await this.fetchPositions();
        } else {
          this.$store.dispatch("NOTIF_WARNING", {
            poruka: this.$t('User_message.m20'),
            position: "center",
            icon: "error",
            timer: 4500,
            width: "550"
          });
        }

      } catch (err) {
        console.error(err);

        this.$store.dispatch("NOTIF_WARNING", {
          poruka: this.$t('User_message.m20'),
          position: "center",
          icon: "error",
          timer: 4500,
          width: "550"
        });
      } finally {
        this.cancelDelete();
      }
    },

    async fetchPositions() {
      this.loading = true;

      try {
        var data = {
          Id: Number(localStorage.getItem('CompanyId')),
          LanguageId: Number(localStorage.getItem('LanguageId'))
        }
        const r = await this.$axios.post('Role/WorkPosition/', data)

        this.positions = r.data

      } catch (err) {
        console.error(this.$t('general.noData'), err)
        this.positions = []
      } finally {
        this.loading = false;
      }
    },

    addPosition() {
      this.editMode = false;
      this.selectedId = null;
      this.form = { name: "" };
      this.dialog = true;

      this.$nextTick(() => {
        this.$refs.workpositionPageForm?.resetValidation()
      })
    },

    editPosition(item) {
      this.editMode = true;
      this.selectedId = item.Id;
      this.form = { name: item.Name };
      this.dialog = true;

      this.$nextTick(() => {
        this.$refs.workpositionPageForm?.resetValidation()
      })
    },

    async savePosition() {
      const { valid } = await this.$refs.workpositionPageForm.validate() // valiadcija forme
      if (!valid) return
      var data = this.editMode
        ? { Id: this.selectedId, Name: this.form.name, CompanyId: Number(localStorage.getItem('CompanyId')) }
        : { Name: this.form.name, CompanyId: Number(localStorage.getItem('CompanyId')) }

      if (this.editMode) {//editovanje WORKPOSITION
        await this.$axios.put('Role/WorkPosition/Edit/', data)
          .then(r => {
            if (r.data == "UPDATED") {
              //PRIKAZ PORUKE
              this.$store.dispatch("NOTIF_SUCCESS", {
                poruka: this.$t('User_message.m17'),
                position: "top-end",
                timer: 2500,
                action: "noreload"
              })

              this.fetchPositions();
            }
            else {
              //PRIKAZ PORUKE
              this.$store.dispatch("NOTIF_WARNING", {
                poruka: this.$t('User_message.m18'),
                position: "center",
                icon: "error",
                timer: 4500,
                width: "550"
              })
            }
          })
      }
      else {//dodavanje nove workposition
        await this.$axios.post('Role/WorkPosition/Add/', data)
          .then(r => {
            if (r.data == "INSERTED") {
              //PRIKAZ PORUKE
              this.$store.dispatch("NOTIF_SUCCESS", {
                poruka: this.$t('User_message.m15'),
                position: "top-end",
                timer: 2500,
                action: "noreload"
              })

              this.fetchPositions();
            }
            else {
              //PRIKAZ PORUKE
              this.$store.dispatch("NOTIF_WARNING", {
                poruka: this.$t('User_message.m16'),
                position: "center",
                icon: "error",
                timer: 4500,
                width: "550"
              })
            }
          })
      }
      this.dialog = false;
      this.fetchPositions();
    },

    closeDialog() {
      this.dialog = false;
      this.resetForm();

      this.$nextTick(() => {
        this.$refs.workpositionPageForm?.resetValidation()
      })
    },
    resetForm() {
      this.form = {
        name: ""
      };
      this.selectedId = null;
    },
  },
};
</script>
